import api from './api';
import { CLOUD_API_URL } from '../config/serverConfig';

export interface PlaylistSummary {
  id: string;
  servicePlaylistId: string;
  name: string;
  description: string | null;
  trackCount: number;
  duration: number;
  imageUrl: string | null;
  isPublic: boolean;
  owner: string;
  source: string;
}

export interface PlaylistTrack {
  id: string;
  serviceTrackId: string;
  title: string;
  albumId: string;
  albumTitle: string;
  artistName: string;
  artistId: string;
  duration: number;
  trackNumber: number;
  discNumber: number;
  hires: boolean;
  streamable: boolean;
  coverUrl: string | null;
  previewUrl: string | null;
  source: string;
  /** Qobuz playlist/get track row id — required to remove a track from that playlist */
  playlistTrackId?: string;
}

export interface PlaylistDetail extends PlaylistSummary {
  tracks: PlaylistTrack[];
}

export interface CurationConfig {
  pinned: string[];
  hidden: string[];
  updated_at?: string;
}

export interface FavoritedPlaylist {
  user_id: string;
  playlist_id: string;
  playlist_name: string;
  cover_art_url: string | null;
  track_count: number;
  favorited_at: string;
}

export const playlistService = {
  getFeatured: (opts?: { type?: string; limit?: number; genreId?: string }) =>
    api.get(
      `/api/streaming/qobuz/playlists/featured?type=${opts?.type || 'editor-picks'}&limit=${opts?.limit || 50}${opts?.genreId ? `&genre_id=${opts.genreId}` : ''}`
    ) as Promise<{ playlists: PlaylistSummary[]; total: number }>,

  search: (query: string, limit = 50) =>
    api.get(
      `/api/streaming/qobuz/playlists/search?query=${encodeURIComponent(query)}&limit=${limit}`
    ) as Promise<{ playlists: PlaylistSummary[]; total: number }>,

  getDetail: (playlistId: string) =>
    api.get(
      `/api/streaming/qobuz/playlists/detail?playlist_id=${encodeURIComponent(playlistId)}`
    ) as Promise<PlaylistDetail>,

  removeTracksFromPlaylist: (playlistId: string, playlistTrackIds: string[]) =>
    api.post('/api/streaming/qobuz/playlists/remove-tracks', {
      playlist_id: playlistId,
      playlist_track_ids: playlistTrackIds,
    }) as Promise<{ success: boolean }>,

  getFavorites: () =>
    api.get('/api/streaming/qobuz/playlist-favorites') as Promise<{ favorites: FavoritedPlaylist[] }>,

  addFavorite: (playlist: { playlist_id: string; playlist_name: string; cover_art_url?: string; track_count?: number }) =>
    api.post('/api/streaming/qobuz/playlist-favorites', playlist),

  removeFavorite: (playlistId: string) =>
    api.delete(`/api/streaming/qobuz/playlist-favorites?playlist_id=${encodeURIComponent(playlistId)}`),

  getCuration: () =>
    api.get('/api/playlists/audiophile-curation') as Promise<CurationConfig>,

  getHubConfig: () =>
    api.get('/api/playlists/hub-config') as Promise<{ sections: any[]; updated_at?: string }>,

  saveHubConfig: async (config: { sections: any[] }, adminKey: string) => {
    const token = (() => {
      try {
        const stored = localStorage.getItem('maestro_auth');
        if (stored) return JSON.parse(stored).accessToken || null;
      } catch {}
      return null;
    })();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Admin-Key': adminKey,
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${CLOUD_API_URL}/api/admin/playlist-hub-config`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(config),
    });
    if (!res.ok) throw new Error(`Save hub config failed: ${res.status}`);
    return res.json();
  },

  saveCuration: async (config: CurationConfig, adminKey: string) => {
    const token = (() => {
      try {
        const stored = localStorage.getItem('maestro_auth');
        if (stored) return JSON.parse(stored).accessToken || null;
      } catch {}
      return null;
    })();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Admin-Key': adminKey,
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${CLOUD_API_URL}/api/admin/audiophile-curation`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(config),
    });
    if (!res.ok) throw new Error(`Save curation failed: ${res.status}`);
    return res.json();
  },
};
